import type { HoldingsBenchmarkCategory, Portfolio, Fund } from 'venn-api';
import {
  ensureCategoryPath,
  appendChildAtPath,
  getAtPath,
  removeAtPath,
  findFundById,
  normalizeGridPath,
  prependChildAtRoot,
  updateAtPath,
} from './portfolioTree';

// Type definitions
export type StrategyPath = HoldingsBenchmarkCategory[];

export enum InvestmentAssignmentState {
  Unmapped = 'unmapped',
  NoStrategy = 'no-strategy',
  Assigned = 'assigned',
}

// Discriminated target for reassigning a node: either locate by fundId or by path
export type ReassignTarget = { type: 'fundId'; fundId: string } | { type: 'path'; path: number[] };

/**
 * Find a strategy node by ID in the holdings categories tree
 */
export const findStrategyNodeById = (
  categories: HoldingsBenchmarkCategory[],
  targetId: string,
): HoldingsBenchmarkCategory | null => {
  for (const category of categories) {
    if (category.id?.id === targetId) {
      return category;
    }
    if (category.children && category.children.length > 0) {
      const found = findStrategyNodeById(category.children, targetId);
      if (found) return found;
    }
  }
  return null;
};

/**
 * DFS to find the path (ancestors -> leaf) for the provided categoryId.
 */
export const findStrategyPath = (categories: HoldingsBenchmarkCategory[], categoryId: number): StrategyPath | null => {
  const dfs = (
    nodes: HoldingsBenchmarkCategory[],
    targetId: number,
    path: HoldingsBenchmarkCategory[] = [],
  ): StrategyPath | null => {
    for (const node of nodes) {
      const currentPath = [...path, node];
      if (node.id?.id === String(targetId)) {
        return currentPath;
      }
      if (node.children && node.children.length) {
        const found = dfs(node.children, targetId, currentPath);
        if (found) return found;
      }
    }
    return null;
  };
  return dfs(categories, categoryId);
};

/**
 * Returns the list of category names along the path to categoryId.
 */
export const buildStrategyPathNames = (
  categories: HoldingsBenchmarkCategory[],
  categoryId: number,
): string[] | null => {
  const path = findStrategyPath(categories, categoryId);
  if (!path) return null;
  return path.map((n) => n.categoryName).filter((name): name is string => Boolean(name));
};

/**
 * Returns all leaf strategies regardless of depth.
 */
export const extractLeafStrategies = (categories: HoldingsBenchmarkCategory[]): HoldingsBenchmarkCategory[] => {
  const leaves: HoldingsBenchmarkCategory[] = [];
  const traverse = (nodes: HoldingsBenchmarkCategory[]) => {
    for (const node of nodes) {
      if (!node.children || node.children.length === 0) {
        leaves.push(node);
      } else {
        traverse(node.children);
      }
    }
  };
  traverse(categories);
  return leaves.sort((a, b) => (a.categoryName || '').localeCompare(b.categoryName || ''));
};

/**
 * Place the given investment node under the provided hierarchy path of category names.
 * Returns an updated root.
 */
export const placeInvestmentUnderHierarchy = (
  root: Portfolio,
  hierarchyLevels: string[],
  investmentNode: Portfolio,
): Portfolio => {
  if (!hierarchyLevels.length) return root;

  const makeStrategyNode = (name: string, parent: Portfolio): Portfolio => ({
    // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
    id: Math.floor(Date.now() * Math.random()), // Random ID for new nodes
    name,
    children: [],
    fund: undefined,
    allocation: 0,
    netAssets: 0,
    cashBalance: 0,
    designation: 'DEFAULT' as const,
    demo: parent.demo,
    draft: parent.draft,
    historical: parent.historical,
    master: parent.master,
    absNetAssets: 0,
    lots: [],
    taxStatuses: [],
  });

  const ensured = ensureCategoryPath(root, hierarchyLevels, makeStrategyNode);
  return appendChildAtPath(ensured.root, ensured.path, { ...investmentNode });
};

/**
 * Centralized re-assignment util: resolve a fund node either via path or fundId, optionally replace fund info,
 * then move it under a provided hierarchy (via names or via categories+categoryId) or prepend at root as fallback.
 */
export const reassignInvestmentNode = (
  root: Portfolio,
  opts: {
    target: ReassignTarget;
    // If provided, will replace the node's fund and name before moving
    replaceFund?: Fund | undefined;
    // Strategy resolution options
    hierarchyLevels?: string[];
    categories?: HoldingsBenchmarkCategory[];
    categoryId?: number;
    // Whether to prepend at root when no hierarchy can be determined (default true)
    fallbackToRootPrepend?: boolean;
  },
): Portfolio => {
  // Resolve node and path
  let resolvedPath: number[] | undefined;
  let resolvedNode: Portfolio | undefined;

  if (opts.target.type === 'fundId') {
    const found = findFundById(root, opts.target.fundId);
    if (found) {
      resolvedPath = found.path;
      resolvedNode = found.node;
    }
  } else {
    const normalized = normalizeGridPath(opts.target.path);
    if (normalized.length > 0) {
      const candidate = getAtPath(root, normalized);
      if (candidate) {
        resolvedPath = normalized;
        resolvedNode = candidate;
      }
    }
  }

  // Fallback: if we still don't have a node but have a path and a replaceFund, just set the fund in place
  if (!resolvedNode) {
    if (opts.target.type === 'path' && opts.replaceFund) {
      const normalized = normalizeGridPath(opts.target.path);
      if (normalized.length > 0) {
        return updateAtPath(root, normalized, (node) => ({
          ...node,
          name: opts.replaceFund!.name,
          fund: opts.replaceFund,
        }));
      }
    }
    return root;
  }

  // Prepare the node to move
  const investmentNode: Portfolio = opts.replaceFund
    ? { ...resolvedNode, name: opts.replaceFund.name, fund: opts.replaceFund }
    : { ...resolvedNode };

  // Remove from original location
  let updatedRoot = removeAtPath(root, resolvedPath!);

  // Determine hierarchy levels
  let levels: string[] | undefined = opts.hierarchyLevels;
  if ((!levels || !levels.length) && opts.categories && opts.categoryId) {
    const names = buildStrategyPathNames(opts.categories, opts.categoryId);
    if (names && names.length) {
      levels = names;
    }
  }

  if (levels && levels.length) {
    updatedRoot = placeInvestmentUnderHierarchy(updatedRoot, levels, investmentNode);
    return updatedRoot;
  }

  // Fallback: prepend at root
  if (opts.fallbackToRootPrepend !== false) {
    updatedRoot = prependChildAtRoot(updatedRoot, investmentNode);
  }
  return updatedRoot;
};

/**
 * Flatten portfolio by removing any strategy/category nodes and keeping only fund nodes directly under the root.
 * Used to ignore user-provided "Strategy" columns for strategy enforcement.
 */
export const stripStrategyNodes = (root: Portfolio): Portfolio => {
  const collectedFunds: Portfolio[] = [];

  const dfs = (node: Portfolio) => {
    if (node.fund) {
      collectedFunds.push({ ...node });
      return;
    }
    node.children?.forEach(dfs);
  };

  dfs(root);

  return {
    ...root,
    children: collectedFunds,
  };
};
